/** @jsx jsx */
import { Grid, IconButton, Spacer, useMediaQuery } from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms"
import { H2, H3, P } from "@cd2/text"
import { doo, genId } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import React, { useRef, useState } from "react"
import { CustomerButton } from "../../Components/Buttons/CustomerButton"
import { brandColors, themeColors } from "../../Components/EOSThemeProvider"
import { CustomerFloatingBox } from "../../Components/Misc/CustomerFloatingBox"
import {
  CustomerSelectList2,
  ICustomerSelect2,
} from "../../Components/SelectLists/CustomerSelectList2"
import { icons } from "../../constants/icons"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { InfoBanner } from "../AlertBanners"
import { eRoutes } from "../../routes"
import { AttachmentsField } from "../../stories/quoteOrders/QuotesOrdersForm"
import { IRecord } from "../../types/schema"
import { skeem } from "../../utils/skeem"
import { formatPrice } from "../../utils/utils"
import { IORBranchIRecord } from "../Branches/BranchesShowPage"
import { PageTitle } from "../CustomerArea/components/PageTitle"
import { useContactCreateForCustomerConfirmation } from "../Customers/CustomerShowContactsPage"
import {
  CopyButton,
  Label,
  OrderLookupField,
  RmaProductRequirementsContext,
  RmaProductSelect,
  SimpleOrderField,
  useRmaProductRequirement,
  ValidateRmaForm,
} from "./BranchRmaForm"
import {
  IRmaAttachment,
  IRmaBranch,
  IRmaContact,
  IRmaCustomer,
  IRmaItem,
  IRmaItemOrder,
  IRmaOrder,
  IRmaProduct,
  IRmaRecord,
  IRmaSubCustomer,
  IRmaSupportTicket,
  rmaItemOrderAttributes,
  rmaProductAttributes,
  RmaRequestType,
  rmaRequestTypes,
  RmaReturnReason,
  rmaReturnReasons,
  rmaStatusesGrouped,
} from "./RmasIndexPage"
import { FloatingBox } from "../../Components/Misc/FloatingBox"

export interface IRmaFormData {
  // association data
  requestType: RmaRequestType
  creditNumber: string
  branch?: IRmaBranch
  items?: IRmaItem[]
  order?: IRmaOrder
  attachments?: IRmaAttachment[]
  customer?: IRmaCustomer
  sub_customer?: IRmaSubCustomer
  contact?: IRmaContact
  assignedTo?: IRecord<"skeem_users">
  supportTicket?: IRmaSupportTicket
  internalRmaBranch?: IORBranchIRecord
  isInternal?: boolean
  created_by?: IRecord<"skeem_users", "name" | "email">

  // attributes
  orderDate: Date
  returnReason: RmaReturnReason
  additionalDetails: string
  technicalCaseNumber: string
  returnsNumber: string
  closedReason: string
  statusMessage: string
  status: (typeof rmaStatusesGrouped)[number]["options"][number]

  // invisible fields // not an actual field but is a real attr
  salesNumber: string
}

export interface IRmaDataFromForm extends Omit<IRmaFormData, "items"> {
  items: Record<string, IRmaItem>
}

export function fakeID() {
  return `temp-${genId()}`
}

export function isFakeID(id: string) {
  return id.startsWith("temp-")
}

function newBaseItem() {
  return {
    id: fakeID(),
    quantity: 1,
    product: undefined,
    serial_number: "",
    tech_case_number: "",
    comment: "",
    item_type: "product" as const,
  }
}

function filterDeletedItems(items: Record<string, IRmaItem>) {
  return Object.entries(items)
    .filter(x => !x[1]._delete)
    .reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {} as Record<string, IRmaItem>)
}

export function CustomerRmaForm({
  onSubmit,
  initialData,
  createdAt,
  refetch: _refetch,
}: {
  onSubmit: (data: IRmaFormData) => Promise<any>
  initialData: Partial<IRmaRecord>
  createdAt?: Date
  refetch?: () => Promise<void> | void
}) {
  const [returnNumberRequired, setReturnNumberRequired] = React.useState(false)

  function formatInitialData(initialData: Partial<IRmaRecord>): Partial<IRmaDataFromForm> {
    return {
      ...initialData,
      items: initialData?.items?.reduce((acc, item) => {
        acc[item.id] = item
        return acc
      }, {} as Record<string, IRmaItem>) ?? { ["1"]: newBaseItem() },
      orderDate: initialData?.orderDate ? new Date(initialData.orderDate) : undefined,
      attachments: initialData?.attachments?.map(attachment => attachment as any) ?? [],
      isInternal: !!initialData?.internalRmaBranch,
      status:
        rmaStatusesGrouped
          .find(x => x.options.find(y => y.value === initialData?.status))
          ?.options.find(y => y.value === initialData?.status) || rmaStatusesGrouped[0].options[0],
    }
  }

  const [contactFormNode, _getContactInformation, _handleCreateContactFromNode] =
    useContactCreateForCustomerConfirmation()

  async function handleSubmit(data: IRmaDataFromForm) {
    const baseData = { ...data, items: Object.values(data.items) }
    return onSubmit(baseData)
  }

  return (
    <React.Fragment>
      {contactFormNode}
      <Form2.Form
        onSubmit={handleSubmit}
        initialData={formatInitialData(initialData)}
        onError={console.error}
      >
        <ValidateRmaForm setReturnNumberRequired={setReturnNumberRequired} createdAt={createdAt}>
        <PageTitle
          breadcrumbs={[
            { label: "Home", to: "/" },
            { label: "Support and Returns", to: eRoutes["/support-and-returns"] },
            { label: "UK Support and Returns", to: eRoutes["/support-and-returns/uk"] },
            { label: "Your Return Requests", to: eRoutes["/rma"] },
            { label: "RMA Form", to: eRoutes["/rma/new"] },
          ]}
          title={`RMA Form`}
          actions={
            <Form2.WithForm
              render={form => (
                <CustomerButton
                  leftIcon={icons.save}
                  disabled={form.state.isSubmitting}
                  type="submit"
                >
                  Submit
                </CustomerButton>
              )}
              subscribe={{ dataChange: true, submitting: true }}
            />
          }
        />
        <Form2.WithForm
          subscribe={{ dataChange: true, submitting: true }}
          render={form => {
            const itemsField = Form2.useField<Record<string, IRmaItem>>("items", {
              defaultValue: {},
              subscribe: true,
            })
            const attachmentsField = Form2.useField<IRmaAttachment[] | undefined>("attachments", {
              subscribe: true,
              defaultValue: [],
            })
            const customerField = Form2.useField<IRmaCustomer | undefined>("customer", {
              subscribe: true,
            })
            const returnReasonField = Form2.useField<RmaReturnReason | undefined>("returnReason", {
              subscribe: true,
            })

            const isReceivingBranch = false

            const saveErrors = Form2.useField("saveErrors", { subscribe: true })
            const productRequirements = React.useContext(RmaProductRequirementsContext)

            useAsyncEffect(async () => {
              if (!customerField.getValue()) {
                return
              }
              const record = await skeem.fetch2<ICustomerSelect2>({
                first: {
                  name: "customers",
                  attributes: CustomerSelectList2.attributes,
                  filter: { matches: { id: customerField.getValue()?.id } },
                },
              })
              if (record.branch) {
                form.getField("branch").setValue(record.branch)
              }
            }, [customerField.getValue()])

            return (
              <div>
                <Grid
                  css={css`
                    background-color: transparent;
                  `}
                >
                  <div>
                    <Grid gap={16}>
                      <CustomerFloatingBox
                        header={
                          <CustomerFloatingBox.Header>
                            <H2>Order</H2>
                          </CustomerFloatingBox.Header>
                        }
                      >
                        <Grid gap={8}>
                          <OrderLookupField customerView={true} />
                          <Form2.Date
                            field="orderDate"
                            label={<Label optional>Order Date</Label>}
                            placeholderText="01/01/2025"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                          <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
                            <Form2.Select
                              field="returnReason"
                              options={rmaReturnReasons}
                              valueExtractor={(opt: { value: RmaReturnReason }) => opt.value}
                              validations={{ presence: true }}
                            />

                            <Form2.Select
                              field="requestType"
                              options={rmaRequestTypes(false)}
                              valueExtractor={(opt: { value: RmaRequestType }) => opt.value}
                              validations={{ presence: true }}
                            />
                          </Grid>
                        </Grid>
                      </CustomerFloatingBox>

                      <CustomerFloatingBox
                        header={
                          <CustomerFloatingBox.Header>
                            <H2>Product Details</H2>
                          </CustomerFloatingBox.Header>
                        }
                        css={
                          itemsField.getError()?.all
                            ? css`
                                ${FloatingBox.vars.borderColor}: red;
                              `
                            : undefined
                        }
                        footer={
                          <div
                            css={css`
                              text-align: right;
                            `}
                          >
                            <CustomerButton
                              leftIcon={icons.plus}
                              onClick={() => {
                                const nextIdx = genId()
                                itemsField.setValue({
                                  ...itemsField.getValue(),
                                  [nextIdx.toString()]: newBaseItem(),
                                })
                              }}
                            >
                              Add item
                            </CustomerButton>
                          </div>
                        }
                      >
                        {returnReasonField.getValue() !== RmaReturnReason.NOT_NEEDED &&
                          Object.values(filterDeletedItems(itemsField.getValue())).some(item => {
                            const requirement = item.product?.id
                              ? productRequirements[item.product.id]
                              : undefined
                            return requirement?.techCaseRequired && !item.tech_case_number
                          }) && (
                          <H3 color={brandColors.red}>
                            Your order includes items that require a Fortus technical case number.
                          </H3>
                        )}
                        <Grid gap={8}>
                          {Object.keys(filterDeletedItems(itemsField.getValue()))?.map(refId => (
                            <ItemField
                              key={refId}
                              refId={refId}
                              isReceivingBranch={isReceivingBranch}
                              customerView={true}
                            />
                          ))}
                          {/* </Grid> */}
                        </Grid>
                        {itemsField.getError()?.all && (
                          <React.Fragment>
                            <Spacer />
                            <P color="error">{itemsField.getError()?.all}</P>
                          </React.Fragment>
                        )}
                        {doo(() => {
                          const items = filterDeletedItems(itemsField.getValue())
                          const returnMessages = Object.values(items)
                            .map(item => item.product?.product_group?.rma_return_message)
                            .filter(Boolean)
                            .filter((a, b, c) => c.indexOf(a) === b) as string[]
                          if (returnMessages.length > 0) {
                            return (
                              <InfoBanner
                                heading="Important Return Information"
                                message={
                                  <div>
                                    {returnMessages.map(x => (
                                      <P key={x}>{x}</P>
                                    ))}
                                  </div>
                                }
                              />
                            )
                          }
                          return null
                        })}
                      </CustomerFloatingBox>

                      <CustomerFloatingBox
                        header={
                          <CustomerFloatingBox.Header>
                            <H2>RMA Details</H2>
                          </CustomerFloatingBox.Header>
                        }
                      >
                        <Grid gap={8}>
                          <Form2.Text field="creditNumber" />
                          <Form2.TextArea
                            field="additionalDetails"
                            rows="auto"
                            placeholder="Enter any more information..."
                          />
                          <Form2.Text field="technicalCaseNumber" placeholder="01234B" />
                          <Form2.Text
                            field="returnsNumber"
                            label="Supplier Returns Number"
                            placeholder="RMA12345"
                            validations={{ presence: returnNumberRequired }}
                          />
                        </Grid>
                      </CustomerFloatingBox>

                      <CustomerFloatingBox
                        header={
                          <CustomerFloatingBox.Header>
                            <H2>Attachments</H2>
                          </CustomerFloatingBox.Header>
                        }
                      >
                        <AttachmentsField
                          onAddFile={file => {
                            attachmentsField.setValue([
                              ...(attachmentsField.getValue() ?? []),
                              { id: fakeID(), ...file },
                            ])
                          }}
                          onRemove={fileIds => {
                            attachmentsField.setValue(
                              (attachmentsField.getValue() ?? []).filter(
                                (file: { fileboyId: string }) => !fileIds.includes(file.fileboyId),
                              ),
                            )
                          }}
                          value={attachmentsField.getValue() ?? []}
                        />
                      </CustomerFloatingBox>
                    </Grid>
                  </div>
                </Grid>

                {saveErrors.getError() && saveErrors.getError().length > 0 && (
                  <React.Fragment>
                    <Spacer />
                    <div
                      css={css`
                        border: 2px solid ${themeColors.alertRed};
                        padding: 16px;
                        border-radius: 3px;
                      `}
                    >
                      {saveErrors.getError().map((error: string) => (
                        <P key={error} color="red">
                          {error}
                        </P>
                      ))}
                    </div>
                  </React.Fragment>
                )}
                <Spacer />
              </div>
            )
          }}
        />
        <div
          css={css`
            padding: 0 12px 12px;
          `}
        >
          <Form2.WithForm
            render={form => (
              <CustomerButton
                leftIcon={icons.save}
                disabled={form.state.isSubmitting}
                type="submit"
              >
                Submit
              </CustomerButton>
            )}
            subscribe={{ dataChange: true, submitting: true }}
          />
        </div>
        </ValidateRmaForm>
      </Form2.Form>
    </React.Fragment>
  )
}

type ISerialNumber = IRecord<"serial_numbers", "serial_number"> & { product: IRmaProduct }

function ItemField({
  refId,
  isReceivingBranch,
  customerView,
}: {
  refId: string
  isReceivingBranch?: boolean
  customerView?: boolean
}) {
  const isTablet = useMediaQuery({ maxWidth: 1200 })
  const branch = Form2.useField<IRmaBranch | undefined>("branch", { subscribe: true })
  const orderField = Form2.useField<IRmaOrder | undefined>("order", { subscribe: true })
  const itemsField = Form2.useField<Record<string, IRmaItem>>("items", {
    subscribe: true,
    defaultValue: {},
  })
  const customerField = Form2.useField<IRmaCustomer | undefined>("customer", { subscribe: true })
  const returnReasonField = Form2.useField<RmaReturnReason | undefined>("returnReason", {
    subscribe: true,
  })
  const productRequirement = useRmaProductRequirement(
    itemsField.getValue()?.[refId]?.product?.id,
  )

  const [serialNumbers, setSerialNumbers] = useState<ISerialNumber[] | null>()

  // for regular select when a product is selected ~ load serial numbers & set if only has 1.
  async function loadSerialNumbers(product?: { id: string }) {
    if (!product) {
      setSerialNumbers(null)
      return
    }
    let serialNumbers = await skeem.fetch2<ISerialNumber[]>({
      model: {
        name: "serial_numbers",
        attributes: ["serial_number", { name: "product", attributes: rmaProductAttributes }],
        filter: { matches: { product: product.id } },
        paginate: { size: 30 },
      },
    })
    // filter only to this branch and remove any dupes.
    serialNumbers = serialNumbers
      .filter(x => x.product.branch?.id === branch.getValue()?.id)
      .filter(
        (item, index, arr) => arr.findIndex(v => v.serial_number === item.serial_number) === index,
      )
    if (serialNumbers.length === 1) {
      itemsField.setValue({
        ...itemsField.getValue(),
        [refId]: {
          ...itemsField.getValue()[refId],
          serial_number: serialNumbers[0].serial_number,
        },
      })
    }
    setSerialNumbers(serialNumbers)
  }

  // for async select when a product is not selected
  async function lookupSerialNumber(serial: string) {
    if (!serial || serial.length < 3) {
      setSerialNumbers([])
      return []
    }
    const order = orderField.getValue() || itemsField.getValue()[refId]?.order
    const productValue = itemsField.getValue()[refId]?.product
    const queryFilter = {
      matches: { serial_number: { like: serial } },
    }
    const andFilter = [queryFilter] as any[]

    andFilter.push({
      gte: [
        {
          count: {
            path: [
              {
                name: "product",
                filter: order
                  ? {
                      gte: [
                        {
                          count: {
                            path: [
                              "order_items",
                              { name: "order", filter: { matches: { id: order.id } } },
                            ],
                          },
                        },
                        { value: 1 },
                      ],
                    }
                  : undefined,
              },
            ],
          },
        },
        { value: 1 },
      ],
    })
    if (productValue) {
      andFilter.push({ matches: { product: productValue.id } })
    }
    const numbers = await skeem.fetch2<ISerialNumber[]>({
      model: {
        name: "serial_numbers",
        attributes: ["serial_number", { name: "product", attributes: rmaProductAttributes }],
        filter: { and: andFilter },
        paginate: { size: 30 },
      },
    })
    const filtered = numbers.filter(n => n.product?.branch?.id === branch.getValue()?.id)
    setSerialNumbers(filtered)
    return filtered
  }

  function handleChangeProduct(product?: any) {
    loadSerialNumbers(product)
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        product,
      },
    })
    checkIfSerialNumberIsRequired()
  }

  function handleChangeQuantity(qty: number | null) {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        quantity: qty,
      },
    })
  }

  function handleChangeTechCaseNumber(nmbr: string) {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        tech_case_number: nmbr,
      },
    })
    checkIfTechCaseNumberRequired()
  }

  function checkIfTechCaseNumberRequired() {
    const currentFieldValue = itemsField.getValue()?.[refId]
    const techCaseNumber = currentFieldValue?.tech_case_number

    if (techCaseNumber?.trim() || returnReasonField.getValue() === RmaReturnReason.NOT_NEEDED) {
      setError({ tech_case_number: undefined })
      return
    }

    if (productRequirement?.techCaseRequired) {
      setError({ tech_case_number: "Tech case number is required for this product" })
      return
    }
    setError({ tech_case_number: undefined })
  }

  const handleChangeSerialNumber = useAsyncCallback(async (serialNumber: { value: string }) => {
    const itemFieldValue = itemsField.getValue()
    const currentFieldValue = itemFieldValue[refId]
    itemsField.setValue({
      ...itemFieldValue,
      [refId]: {
        ...currentFieldValue,
        serial_number: serialNumber?.value ?? "",
      },
    })
    checkIfSerialNumberIsRequired()
    if (!serialNumber?.value) {
      return
    }

    // Attempt to find the order and product details based on the serial number
    const customerId = customerField.getValue()?.id

    type SerialNumber = IRecord<"serial_numbers"> & {
      product: IRmaProduct | null
      order_item: IRecord<"order_items"> & { order: IRmaItemOrder }
    }
    const records = await skeem.fetch2<SerialNumber[]>({
      model: {
        name: "serial_numbers",
        attributes: [
          { name: "product", attributes: rmaProductAttributes },
          {
            name: "order_item",
            attributes: [{ name: "order", attributes: rmaItemOrderAttributes }],
          },
        ],
        filter: {
          and: [
            { matches: { serial_number: serialNumber.value } },
            customerId
              ? { eq: [{ path: ["order_item", "order", "customer", "id"] }, { value: customerId }] }
              : undefined,
          ].filter(Boolean),
        },
      },
    })
    if (records.length === 1) {
      const record = records[0]
      if (record.product)
        itemsField.setValue({
          ...itemsField.getValue(),
          [refId]: {
            ...itemsField.getValue()[refId],
            product: record.product,
            order: record.order_item.order,
          },
        })
    }
    console.log("SN", records)
  })

  function handleRemoveItem() {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: { ...itemsField.getValue()[refId], _delete: true },
    })
  }

  async function checkIfSerialNumberIsRequired() {
    const itemFieldValue = itemsField.getValue()
    const currentFieldValue = itemFieldValue[refId]

    const product = currentFieldValue.product
    const serialNumber = currentFieldValue.serial_number

    if (serialNumber || !product) {
      // if we have a serial number or no product, we can assume it's valid
      setError({ serial_number: undefined })
      return
    }

    const productRecord = await window.skeem.fetch2<
      IRecord<"products"> & {
        product_group?: IRecord<"product_groups"> & {
          product_group_master?: IRecord<"product_group_masters", "rma_mandatory_serial_number">
        }
      }
    >({
      first: {
        name: "products",
        matches: { id: product.id },
        attributes: [
          {
            name: "product_group",
            attributes: [
              { name: "product_group_master", attributes: ["rma_mandatory_serial_number"] },
            ],
          },
        ],
      },
    })
    if (
      productRecord?.product_group?.product_group_master?.rma_mandatory_serial_number &&
      !serialNumber
    ) {
      setError({ serial_number: "Serial number is required for this product" })
      return
    }
    setError({ serial_number: undefined })
    return
  }

  function handleChangeOrder(order?: IRmaItemOrder) {
    itemsField.setValue({
      ...itemsField.getValue(),
      [refId]: {
        ...itemsField.getValue()[refId],
        order,
      },
    })
  }

  const value = itemsField.getValue()?.[refId]
  const error = itemsField.getError()?.[refId] as Record<string, string> | undefined

  useAsyncEffect(async () => {
    if (value.product) {
      await loadSerialNumbers(value.product)
    }
  }, [branch.getValue()?.id, orderField.getValue()?.id, value?.product?.id])

  const orderItems = orderField
    .getValue()
    ?.items?.filter(item => item.product?.id === value.product?.id)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  function debouncedSearchSerialNumber(serial: string) {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(async () => {
      await lookupSerialNumber(serial ?? "")
    }, 150)
    return serial
  }

  function setError(errorData?: Record<string, any>) {
    const value = itemsField.getError() ?? {}
    itemsField.setError({
      ...value,
      [refId]: { ...value[refId], ...errorData },
    })
  }

  const hasOrder = !!orderField.getValue()?.id

  const cols = isTablet
    ? undefined
    : [...(hasOrder ? [{ fr: 1 }, 120] : [{ fr: 1 }, 120, { fr: 1 }]), 36]

  if (customerView) {
    const customerCols = isTablet
      ? undefined
      : [{ fr: 1 }, { fr: 1 }, { fr: 1 }, ...(hasOrder ? [36] : [{ fr: 1 }, 36])]
    return (
      <CustomerFloatingBox
        color={"white"}
        css={
          isTablet
            ? css`
                border: 1px solid #00000066;
                border-radius: 6px;
                padding: 1px;
              `
            : undefined
        }
        sidePaddingOverride={16}
      >
        <Grid columns={customerCols} gap={16}>
          <RmaProductSelect
            onChange={handleChangeProduct}
            value={value.product}
            error={error?.product}
            includeLink={!customerView}
            order={value.order}
          />
          <div>
            <Fields.CreatableSelect
              label="Serial Number"
              onChange={opt => handleChangeSerialNumber(opt)}
              options={[value?.serial_number, ...(serialNumbers?.map(x => x.serial_number) ?? [])]
                .filter(Boolean)
                .map(x => ({ label: x, value: x }))}
              value={
                value?.serial_number
                  ? { label: value.serial_number, value: value.serial_number }
                  : undefined
              }
              error={error?.serial_number}
              onCreateOption={opt => handleChangeSerialNumber({ value: opt })}
              onInputChange={
                value.product ? undefined : value => debouncedSearchSerialNumber(value)
              }
              getNewOptionData={opt => ({ label: opt, value: opt })}
              createOptionPosition="first"
              placeholder="Select or enter new..."
              isClearable
            />
            {!!value.serial_number && (
              <div
                css={css`
                  margin-top: 4px;
                `}
              >
                <CopyButton value={value.serial_number} />
              </div>
            )}

            {productRequirement?.techCaseRequired &&
              returnReasonField.getValue() !== RmaReturnReason.NOT_NEEDED && (
                <div>
                  <Spacer />
                  <Fields.Text
                    label="Tech Case Number"
                    onChange={handleChangeTechCaseNumber}
                    value={value.tech_case_number}
                    error={error?.tech_case_number}
                    showRequiredStar
                  />
                </div>
              )}
          </div>
          <Fields.Number2
            label="Quantity"
            onChange={handleChangeQuantity}
            value={value.quantity}
            error={error?.quantity}
            showRequiredStar
          />
          {!hasOrder && (
            <SimpleOrderField
              key={value.id}
              onChange={handleChangeOrder}
              value={value.order}
              error={error?.order}
              product={value.product}
              customerView={customerView}
            />
          )}
          {isTablet ? (
            <CustomerButton onClick={handleRemoveItem}>Remove Item</CustomerButton>
          ) : (
            <div
              css={css`
                margin-top: 24px;
              `}
            >
              <IconButton
                onClick={handleRemoveItem}
                icon={icons.x}
                buttonColor="red"
                buttonStyle="outline"
                square
              />
            </div>
          )}
        </Grid>
      </CustomerFloatingBox>
    )
  }

  return (
    <CustomerFloatingBox color={themeColors.lightestGrey}>
      <Grid gap={16}>
        <Grid columns={[{ fr: 1 }, 36]} alignItems={value.product ? "start" : "end"} gap={8}>
          <RmaProductSelect
            onChange={handleChangeProduct}
            value={value.product}
            error={error?.product}
            includeLink={!customerView}
            order={value.order}
          />
          <IconButton
            onClick={handleRemoveItem}
            icon={icons.x}
            buttonColor="red"
            buttonStyle="outline"
            square
          />
        </Grid>
        <Grid columns={cols} gap={8}>
          <div>
            <Fields.CreatableSelect
              label="Serial Number"
              onChange={opt => handleChangeSerialNumber(opt)}
              options={[value?.serial_number, ...(serialNumbers?.map(x => x.serial_number) ?? [])]
                .filter(Boolean)
                .map(x => ({ label: x, value: x }))}
              value={
                value?.serial_number
                  ? { label: value.serial_number, value: value.serial_number }
                  : undefined
              }
              error={error?.serial_number}
              onCreateOption={opt => handleChangeSerialNumber({ value: opt })}
              onInputChange={
                value.product ? undefined : value => debouncedSearchSerialNumber(value)
              }
              getNewOptionData={opt => ({ label: opt, value: opt })}
              createOptionPosition="first"
              placeholder="Select or enter new..."
              isClearable
            />
            {!!value.serial_number && (
              <div
                css={css`
                  margin-top: 4px;
                `}
              >
                <CopyButton value={value.serial_number} />
              </div>
            )}
          </div>
          <Fields.Number2
            label="Quantity"
            onChange={handleChangeQuantity}
            value={value.quantity}
            error={error?.quantity}
            showRequiredStar
          />
          {!hasOrder && (
            <SimpleOrderField
              key={value.id}
              onChange={handleChangeOrder}
              value={value.order}
              error={error?.order}
              product={value.product}
              customerView={customerView}
            />
          )}
        </Grid>
        {productRequirement?.techCaseRequired &&
          returnReasonField.getValue() !== RmaReturnReason.NOT_NEEDED && (
            <Fields.Text
              label="Tech Case Number"
              onChange={handleChangeTechCaseNumber}
              value={value.tech_case_number}
              error={error?.tech_case_number}
              showRequiredStar
            />
          )}
        {isReceivingBranch && orderItems && orderItems?.length > 0 && (
          <div>
            <P>{orderItems?.length} matching order items</P>
            {orderItems?.map(item => (
              <div key={item.id}>
                Cost price:{" "}
                {formatPrice(item.cost_price_pennies ?? 0, { isPennies: true, currency: "" })};
                Price: {formatPrice(item.price_pennies ?? 0, { isPennies: true, currency: "" })}
              </div>
            ))}
          </div>
        )}
      </Grid>
    </CustomerFloatingBox>
  )
}
